import type { CMSAssetMetrics, CMSAssetItem } from '../../types';
import type { CardMetadataItem } from '@workramp/ui';
import type { LinkCardProps } from '~/elc/features/content-cards';
import { useTheme } from 'styled-components';
import { I18n } from '~/common/explicit-exports';
import { LinkCard } from '~/elc/features/content-cards';
import { useCreateAssetAuditData } from '../../api';
import { CMSAssetAuditAction, SHOW_METRICS_THRESHOLD } from '../../constants';
import { getIconPropsForCMSAsset } from '../../utils';
import { CMSShareButton } from '../CMSShareButton';

export interface CmsLinkCardProps extends Omit<LinkCardProps, 'iconName' | 'iconColor' | 'metadata' | 'errorImageUrl'> {
  assetId: string;
  topLevelContentId: string;
  metrics: CMSAssetMetrics;
  allowExternalSharing: boolean;
  onAuditDataUpdated?: () => Promise<void> | void;
  onCopy?: () => void;
}

const { APP_CONSTANTS } = window;

export function CmsLinkCard({
  assetId,
  topLevelContentId,
  onPress,
  onCopy,
  onAuditDataUpdated,
  metrics,
  actions,
  allowExternalSharing,
  url,
  ...props
}: CmsLinkCardProps) {
  const theme = useTheme();
  const SHARE_LINK = I18n.t('js.learner.libraries.share_link');
  const { color: iconColor, name: iconName } = getIconPropsForCMSAsset(
    { assetType: APP_CONSTANTS.CMS_ASSET_TYPES.LINK } as CMSAssetItem,
    theme.colors,
  );

  const createCmsAssetAuditData = useCreateAssetAuditData();

  const metadata: Array<CardMetadataItem> = [];
  if (metrics.totalViews > SHOW_METRICS_THRESHOLD) {
    metadata.push({
      iconName: 'eye-open:regular',
      text: I18n.t('js.learner.libraries.metric_views', { views: metrics.totalViews }),
      textColor: theme.colors.on[600],
      iconColor: theme.colors.on[600],
    });
  }
  if (metrics.totalShares > SHOW_METRICS_THRESHOLD) {
    metadata.push({
      iconName: 'link:regular',
      text: I18n.t('js.learner.libraries.metric_shares', { shares: metrics.totalShares }),
      textColor: theme.colors.on[600],
      iconColor: theme.colors.on[600],
    });
  }

  const handleCopy = () => {
    onCopy?.();
    createCmsAssetAuditData.mutate(
      {
        topLevelContentId,
        assetId,
        assetType: APP_CONSTANTS.CMS_ASSET_TYPES.LINK,
        actionType: CMSAssetAuditAction.DOWNLOAD,
      },
      {
        onSuccess: onAuditDataUpdated,
      },
    );
  };

  const cmsActions = actions ? (
    <>
      <CMSShareButton
        asset={{
          id: assetId,
          assetType: APP_CONSTANTS.CMS_ASSET_TYPES.LINK,
          allowExternalSharing,
          allowInternalSharing: true,
        }}
        iconButton
        onCopy={handleCopy}
        tooltipText={SHARE_LINK}
        url={url}
      />
      {actions}
    </>
  ) : null;

  return (
    <LinkCard
      onPress={onPress}
      iconColor={iconColor}
      iconName={iconName}
      metadata={metadata}
      errorImageUrl={APP_CONSTANTS.DEFAULT_COVER_IMAGES.CMS_LINK}
      actions={cmsActions}
      url={url}
      {...props}
    />
  );
}
